import React, { useState, useEffect, useCallback } from 'react';
import { LayoutDashboard } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import TimeframeToggle from '../components/dashboard/TimeframeToggle';
import WorldClock from '../components/dashboard/WorldClock';
import PtoBalanceWidget from '../components/dashboard/PtoBalanceWidget';
import UpcomingHolidays from '../components/dashboard/UpcomingHolidays';
import UpcomingTimeOff from '../components/dashboard/UpcomingTimeOff';
import UpcomingBirthdays from '../components/dashboard/UpcomingBirthdays';
import ModuleCards from '../components/dashboard/ModuleCards';
import './Dashboard.css';

function Dashboard() {
  const { user } = useAuth();
  const firstName = user?.first_name || user?.name?.split(' ')[0] || 'there';

  const [timeframe, setTimeframe] = useState('week');
  const [countries, setCountries] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [timeoff, setTimeoff] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [loadingFeeds, setLoadingFeeds] = useState(true);
  const [loadingClock, setLoadingClock] = useState(true);

  // Fetch world clock data once (doesn't depend on timeframe)
  useEffect(() => {
    let cancelled = false;
    async function fetchClock() {
      try {
        const data = await api.get('/dashboard/world-clock');
        if (!cancelled) setCountries(data.countries || []);
      } catch (err) {
        // Silent fail -- world clock is best-effort
        if (!cancelled) setCountries([]);
      } finally {
        if (!cancelled) setLoadingClock(false);
      }
    }
    fetchClock();
    return () => { cancelled = true; };
  }, []);

  // Fetch feeds when timeframe changes
  const fetchFeeds = useCallback(async (range) => {
    setLoadingFeeds(true);
    try {
      const [holidayData, timeoffData, birthdayData] = await Promise.allSettled([
        api.get(`/dashboard/upcoming-holidays?range=${range}`),
        api.get(`/dashboard/upcoming-timeoff?range=${range}`),
        api.get(`/dashboard/upcoming-birthdays?range=${range}`),
      ]);
      setHolidays(holidayData.status === 'fulfilled' ? (holidayData.value.holidays || []) : []);
      setTimeoff(timeoffData.status === 'fulfilled' ? (timeoffData.value.timeoff || []) : []);
      setBirthdays(birthdayData.status === 'fulfilled' ? (birthdayData.value.birthdays || []) : []);
    } catch (err) {
      // Catch-all -- clear data rather than looping
      setHolidays([]);
      setTimeoff([]);
      setBirthdays([]);
    } finally {
      setLoadingFeeds(false);
    }
  }, []);

  useEffect(() => {
    fetchFeeds(timeframe);
  }, [timeframe, fetchFeeds]);

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
  };

  const todayFormatted = format(new Date(), 'EEEE, MMMM d, yyyy');

  return (
    <div className="dashboard">
      {/* Welcome Header */}
      <div className="dashboard-welcome-header">
        <div className="dashboard-welcome-left">
          <div className="page-header">
            <LayoutDashboard size={24} strokeWidth={2} />
            <h1>Dashboard</h1>
          </div>
          <div className="dashboard-welcome">
            <h2>Welcome back, {firstName}</h2>
            <p>{todayFormatted}</p>
          </div>
        </div>
        <div className="dashboard-welcome-right">
          <TimeframeToggle value={timeframe} onChange={handleTimeframeChange} />
        </div>
      </div>

      {/* World Clock -- always show when countries are loaded */}
      {!loadingClock && countries.length > 0 && (
        <WorldClock countries={countries} />
      )}

      {/* PTO Balance */}
      <PtoBalanceWidget />

      {/* Feed Columns: Holidays + Time Off */}
      <div className="dashboard-feeds">
        <UpcomingHolidays
          holidays={holidays}
          loading={loadingFeeds}
          timeframe={timeframe}
        />
        <UpcomingTimeOff
          timeoff={timeoff}
          loading={loadingFeeds}
          timeframe={timeframe}
        />
      </div>

      {/* Birthdays */}
      <UpcomingBirthdays
        birthdays={birthdays}
        loading={loadingFeeds}
        timeframe={timeframe}
      />

      {/* Module Cards */}
      <ModuleCards />
    </div>
  );
}

export default Dashboard;
